import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ApparelOrder } from "@/types/models";
import ApparelService from "@/services/apparelService";
import { getCurrentOrgId } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import OrderDetailsDialog from "@/components/apparel/orders/OrderDetailsDialog";
import OrdersTable from "@/components/apparel/orders/OrdersTable";
import { motion } from "framer-motion";

const ApparelOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ApparelOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<ApparelOrder | null>(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [invoices, setInvoices] = useState<any[]>([]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) {
        toast.error("Unable to determine organization");
        return;
      }
      
      const ordersData = await ApparelService.getOrganizationOrders(orgId);
      setOrders(ordersData);
      
      const { data: invoicesData, error: invoicesError } = await supabase
        .from("invoices")
        .select("*")
        .eq("organization_id", orgId)
        .eq("status", "draft")
        .order("created_at", { ascending: false });
      
      if (invoicesError) throw invoicesError;
      setInvoices(invoicesData as any[]);
    } catch (error) {
      console.error("Error loading orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleViewOrder = async (order: ApparelOrder) => {
    try {
      const fullOrder = await ApparelService.getOrderById(order.id);
      if (fullOrder) {
        setSelectedOrder(fullOrder);
        setOrderDetailsOpen(true);
      } else {
        toast.error("Failed to load order details");
      }
    } catch (error) {
      console.error("Error loading order details:", error);
      toast.error("Failed to load order details");
    }
  };

  const handleUpdateStatus = async (orderId: string, status: string) => {
    try {
      const success = await ApparelService.updateOrderStatus(orderId, status);
      if (success) {
        toast.success(`Order status updated to ${status}`);
        await loadOrders();
        
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder({
            ...selectedOrder,
            status,
          });
        }
      } else {
        toast.error("Failed to update order status");
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleOrderUpdated = async () => {
    setOrderDetailsOpen(false);
    await loadOrders();
  };

  const filteredOrders = orders.filter(order => {
    if (filterStatus !== "all" && order.status !== filterStatus) {
      return false;
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        order.family?.name?.toLowerCase().includes(term) ||
        (order.student?.first_name + ' ' + order.student?.last_name)?.toLowerCase().includes(term) ||
        order.id.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  return (
    <motion.div
      className="max-w-7xl mx-auto px-4 md:px-6 py-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="rounded-3xl shadow-2xl border border-slate-200/50 bg-gradient-to-br from-white via-slate-50/30 to-white backdrop-blur-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-8 py-8 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Order Management</h1>
              <p className="text-emerald-100 text-lg">
                Track and manage all apparel and merchandise orders
              </p>
            </div>
            <div className="flex gap-3">
              <motion.button
                onClick={loadOrders}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg transition-all duration-300 flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-lg">↻</span>
                Refresh
              </motion.button>
              <motion.button
                onClick={() => navigate("/apparel-shop")}
                className="bg-white text-emerald-600 hover:bg-emerald-50 px-6 py-2.5 rounded-xl font-semibold shadow-lg transition-all duration-300 flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-lg">+</span>
                Create Order
              </motion.button>
            </div>
          </div>
        </div>
        <div className="p-8">
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 mb-8 border border-slate-200/50">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Search Orders</label>
                <Input
                  placeholder="Search by family name, student, or order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="shadow-sm border-slate-300 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>
              <div className="lg:w-64">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Filter Status</label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="shadow-sm border-slate-300 focus:border-blue-500 focus:ring-blue-500/20">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="ready">Ready for Pickup</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600 font-medium">Loading orders...</p>
              </div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📦</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Orders Found</h3>
                <p className="text-slate-600 mb-6">
                  {searchTerm || filterStatus !== "all" 
                    ? "No orders match your current search or filter criteria." 
                    : "You haven't received any orders yet."}
                </p>
                <motion.button
                  onClick={() => navigate("/apparel-shop")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Create Your First Order
                </motion.button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
              <OrdersTable
                orders={filteredOrders}
                onViewOrder={handleViewOrder}
              />
            </div>
          )}
        </div>
      </motion.div>
      <OrderDetailsDialog
        open={orderDetailsOpen}
        onOpenChange={setOrderDetailsOpen}
        order={selectedOrder}
        invoices={invoices}
        onUpdateStatus={handleUpdateStatus}
        onOrderUpdated={handleOrderUpdated}
      />
    </motion.div>
  );
};

export default ApparelOrders;
